'use strict';

interface MfaField {
    id : string;
    label? : string;
    format? : string;
    presentation? : string;
    imageSource? : string;
}

interface MfaScreen {
    id : string;
    fields? : MfaField[];
}

interface MfaButton {
    id : string;
    label : string;
}

interface MfaMessage {
    code : string;
    msgStatus? : string;
    msgType? : string;
    body? : string;
    buttons? : MfaButton[];
    screens? : MfaScreen[];
}

interface MfaBitmap {
    width : number;
    height : number;
    bits : string;
}

class MfaUtils {
    static readonly CHALLENGE_CODES = [
        'SYS00_MFA_AUTHENTICATION_CODE_EMAIL',
        'SYS00_MFA_AUTHENTICATION_CODE_TOTP',
        'SYS00_MFA_AUTHENTICATION_CODE_TOTP_INIT'
    ];
    static readonly EXPIRED_CODES = [
        'SYS00_MFA_AUTHENTICATION_CODE_EMAIL_EXPIRED',
        'SYS00_MFA_AUTHENTICATION_CODE_TOTP_EXPIRED'
    ];
    static readonly CODE_EMAIL = 'SYS00_MFA_AUTHENTICATION_CODE_EMAIL';
    static readonly SCREEN_EMAIL = 'MFA_EMAIL';
    static readonly SCREEN_TOTP = 'MFA_TOTP';
    static readonly BUTTON_VALIDATE = 'VALIDATE';
    static readonly BUTTON_RESEND = 'RESEND';
    static readonly FIELD_CODE = 'CODE';
    static readonly FIELD_QRCODE = 'PIC';

    private static readonly NEW_LINE = '[N]';
    private static readonly PARAGRAPH_BREAK_PATTERN = /(?:\[N\]){2,}/g;
    private static readonly SINGLE_NEW_LINE_PATTERN = /\[N\]/g;
    private static readonly BITMAP_PATTERN = /^bit:(\d+)x(\d+):([01]+)$/;

    static isMfaChallenge(message : MfaMessage) : boolean {
        return !!message && MfaUtils.CHALLENGE_CODES.indexOf(message.code) >= 0;
    }

    static isMfaExpired(message : MfaMessage) : boolean {
        return !!message && MfaUtils.EXPIRED_CODES.indexOf(message.code) >= 0;
    }

    static getCodeScreen(message : MfaMessage) : MfaScreen | undefined {
        return ((message && message.screens) || []).find((screen) => MfaUtils.getField(screen, MfaUtils.FIELD_CODE) !== undefined);
    }

    static getCodeScreenId(message : MfaMessage) : string {
        let codeScreen = MfaUtils.getCodeScreen(message);
        if (codeScreen !== undefined) {
            return codeScreen.id;
        }
        return message.code === MfaUtils.CODE_EMAIL ? MfaUtils.SCREEN_EMAIL : MfaUtils.SCREEN_TOTP;
    }

    static isEmailChallenge(message : MfaMessage) : boolean {
        return !!message && message.code === MfaUtils.CODE_EMAIL;
    }

    static normalizeCode(code : string, emailChallenge : boolean) : string {
        let trimmed = (code || '').replace(/\s+/g, '');
        return emailChallenge ? trimmed.toUpperCase() : trimmed;
    }

    static getField(screen : MfaScreen, fieldId : string) : MfaField | undefined {
        return ((screen && screen.fields) || []).find((field) => field.id === fieldId);
    }

    static getButton(message : MfaMessage, buttonId : string) : MfaButton | undefined {
        return ((message && message.buttons) || []).find((button) => button.id === buttonId);
    }

    static splitLabel(label : string) : string[] {
        return (label || '').split(MfaUtils.NEW_LINE);
    }

    /**
     * Formats a server text for display: a sequence of two or more new line tokens becomes a
     * paragraph break and an isolated token becomes a space.
     */
    static formatServerText(text : string) : string {
        return (text || '')
            .replace(MfaUtils.PARAGRAPH_BREAK_PATTERN, '\n\n')
            .replace(MfaUtils.SINGLE_NEW_LINE_PATTERN, ' ')
            .trim();
    }

    static parseBitmap(imageSource : string) : MfaBitmap | null {
        let match = MfaUtils.BITMAP_PATTERN.exec(imageSource || '');
        if (match === null) {
            return null;
        }
        let width = parseInt(match[1], 10);
        let height = parseInt(match[2], 10);
        let bits = match[3];
        if (width === 0 || height === 0 || bits.length !== width * height) {
            return null;
        }
        return { width: width, height: height, bits: bits };
    }

    /**
     * Renders the bitmap (column-major, '1' = black) as a PNG data url,
     * surrounded by a white quiet zone, or null when no 2d canvas is available.
     */
    static renderQrCode(bitmap : MfaBitmap, moduleSize : number = 6, quietZone : number = 4) : string | null {
        let canvas = document.createElement('canvas');
        canvas.width = (bitmap.width + 2 * quietZone) * moduleSize;
        canvas.height = (bitmap.height + 2 * quietZone) * moduleSize;
        let context = canvas.getContext('2d');
        if (context === null) {
            return null;
        }
        context.imageSmoothingEnabled = false;
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#000000';
        for (let x = 0; x < bitmap.width; x++) {
            for (let y = 0; y < bitmap.height; y++) {
                if (bitmap.bits.charAt(x * bitmap.height + y) === '1') {
                    context.fillRect((x + quietZone) * moduleSize, (y + quietZone) * moduleSize, moduleSize, moduleSize);
                }
            }
        }
        return canvas.toDataURL('image/png');
    }
}

export default MfaUtils;
export { MfaMessage, MfaScreen, MfaField, MfaButton, MfaBitmap };
